import React, { useEffect, useState } from 'react';
import { Menu } from '@nackle/origami';
import { KAFKA_TOPICS, ASSUMPTION_TYPES } from '../constants';
import { LocaleString } from '@nackle/intl-tools';
import { routeHistory } from '@nackle/biw-web-router';
import { SendEvent } from '../event-proxy.js';
import icons, { Close } from '@nackle/origami-icons';
import hasPermission from '../utils/has-permission.js';

import './left-nav.less';

const LeftNav = ( { contentLoaded, menu, user, piersUser, toggleSidebar, idUrl, companyId, identityAssumption } ) => {
    const [ disabledItems, setDisabledItems ] = useState( [] );
    const handleNavigate = async ( { item, domEvent } ) => {
        //if there's no domEvent it's because someone tabbed to the menu and hit a key that wasn't Enter so ignore it
        if ( !domEvent ) {
            return;
        }

        if ( item.disabled ) {
            return;
        }
        // the url will either be a full url or a relative path. If it's a full url, replace clientSubdomain, otherwise just get the url back
        const url = getUrl( item.props.url );

        const isExternal = url.startsWith( 'https://' );
        // if the url external to umbrella and the user is a delegate
        // first check if it is a link to DMSA
        if ( identityAssumption === ASSUMPTION_TYPES.DELEGATE && isExternal ) {
            const isDmsa = url.indexOf( 'myawardsite' ) > 0;
            if ( isDmsa ) {
                // then check if they have the dmsa elevate permission
                if ( !hasPermission( 'elevate', 'view-service-awards', user?.claims?.permissions ) ) {
                    // is link to DMSA, but user doesn't have permission
                    return;
                }
            } else {
                // is not link to DMSA
                return;
            }
        }
        const { session_id, access_token } = user;
        // prevent from sending multiple events when user clicks on enter
        domEvent?.preventDefault();
        //data for ui interaction event
        const eventData = {
            timestamp: Date.now(),
            application: 'web-event-proxy',
            sessionId: session_id,
            companyId: user?.claims?.company_id,
            sessionPersonId: user.claims.person_id,
            navigationLocation: 'Deck',
            action: 'CLICK',
            currentPath: window.location.pathname,
            targetPath: url,
            domReference: JSON.stringify( domEvent?.target.classList )
        };

        const topicName = KAFKA_TOPICS.EXPERIENCE_NAVIGATION;
        if ( window.innerWidth < 1408 ) {
            toggleSidebar();
        }

        await SendEvent( access_token, eventData, topicName );

        //if it's a full url, set the location - might be a link to something else or might not be umbrella
        if ( isExternal ) {
            window.location.href = url;
        } else {
            //otherwise do a routehistory push because it's an umbrella url
            routeHistory.push( `${ url }/` );
        }

    };
    //get the identity for url string replace for nav items when standalone
    const clientSubdomain = piersUser?.solutions[ 0 ]?.solutionIdentity;
    const getUrl = url => {
        let dashboardLink = '';
        const dmsa = piersUser?.solutions[ 0 ]?.experiences.filter( experience => { return experience.name === 'DayMaker Service Awards'; } );
        if ( dmsa?.length > 0 ) {
            dashboardLink = dmsa[ 0 ].url;
        }
        const replacedUrl = url.replace( '${solution.identity}', clientSubdomain ).replace( '${idUrl}', idUrl ).replace( '${companyId}', companyId ).replace( '${dashboardLink}', dashboardLink );

        return replacedUrl;
    };
    let Primary;
    const { items } = menu;

    useEffect( () => {
        if ( items && items.length > 0 ) {
            const disabled = items.map( item =>{

                if ( item.url ) {
                    const url = getUrl( item.url );
                    item.disabled = false;
                    // if the url external to umbrella and the user is a delegate
                    // first check if it is a link to DMSA
                    const isDmsa = url.indexOf( 'host-transfer' ) > 0;
                    if ( isDmsa ) {
                        item.url = item.url + '?clear=true';
                    }
                    if ( identityAssumption === ASSUMPTION_TYPES.DELEGATE ) {
                        const isExternal = url.startsWith( 'https://' );
                        if ( isDmsa ) {
                            // then check if they have the dmsa elevate permission
                            if ( !hasPermission( 'elevate', 'view-service-awards', user?.claims?.permissions ) ) {
                                // is link to DMSA, but user doesn't have permission
                                item.disabled = true;
                            }
                        }
                        if ( isExternal && !isDmsa ) {
                            item.disabled = true;
                        }
                    }
                }
                if ( item.items && item.items.length > 0 ) {
                    item.items.map( child => {
                        if ( child && child.item ) {
                            const childUrl = getUrl( child?.item?.url );
                            child.item.disabled = false;
                            // if the url external to umbrella and the user is a delegate
                            // first check if it is a link to DMSA
                            if ( identityAssumption === ASSUMPTION_TYPES.DELEGATE ) {
                                const isDmsa = childUrl.indexOf( 'host-transfer' ) > 0;
                                const isExternal = childUrl.startsWith( 'https://' );
                                if ( isDmsa ) {
                                    // then check if they have the dmsa elevate permission
                                    if ( !hasPermission( 'elevate', 'view-service-awards', user?.claims?.permissions ) ) {
                                        // is link to DMSA, but user doesn't have permission
                                        child.item.disabled = true;
                                    }
                                }
                                if ( isExternal && !isDmsa ) {
                                    child.item.disabled = true;
                                }
                            }
                        }
                    } );
                }
                return item;
            } );
            setDisabledItems( disabled );
        }
    }, [ items ] );
    //sort the menu items by order
    const sortedItems = disabledItems?.sort( ( a, b ) => { return a.sortOrder > b.sortOrder ? 1 : -1; } );
    const menuItems = [];
    const pathname = window.location.pathname;
    const pathParts = pathname.split( '/' );
    const currentKey = `/${ pathParts[ 1 ] }`;
    //build menuItems array for menu display
    sortedItems?.map( i => {
        if ( i.url || ( i.items && i.items.length > 0 ) ) {
            let icon = [];
            if ( i.iconName ) {
                // get the correct icon - hopefully the way they're named in nav service lines up with the naming from origami-icons
                icon = icons.filter( icon => { return icon.name.toLowerCase() === i.iconName.replace( /\s/g, '' ).toLowerCase(); } );
            }
            // for now just get the enabled menu items that don't have children (secondary menus)
            if ( i.enabled && !i.items ) {
                menuItems.push( { label: <LocaleString code={ i.labelKey } />, key: i.url, icon: icon && icon.length > 0 ? icon[ 0 ].component : null, disabled: i.disabled, className: i.disabled ? 'menu-item' : 'menu-item deck-primary-color deck-primary-hover', url: i.url, tabIndex: 0 } );
            } else if ( i.enabled && i.items && i.items.length > 0 ) {
                const children = [];
                i.items.map( item => {
                    let childIcon = [];
                    if ( item.item.iconName ) {
                        // get the correct icon - hopefully the way they're named in nav service lines up with the naming from origami-icons
                        childIcon = icons.filter( icon => { return icon.name.toLowerCase() === item.item.iconName.replace( /\s/g, '' ).toLowerCase(); } );
                    }
                    if ( item.item.enabled ) {
                        children.push( { label: <LocaleString code={ item.item.labelKey } />, key: item.item.url, icon: childIcon && childIcon.length > 0 ? childIcon[ 0 ].component : null, className: 'menu-item deck-primary-color deck-primary-hover', url: item.item.url, tabIndex: 0 } );
                    }
                } );
                menuItems.push( { label: <LocaleString code={ i.labelKey } />, key: i.url, icon: icon && icon.length > 0 ? icon[ 0 ].component : null, className: 'menu-item deck-primary-color deck-primary-hover', url: i.url, tabIndex: 0, children: children } );
            }
        }
    } );
    if ( menuItems && menuItems.length > 0 ) {
        Primary = <Menu className="nkl-deck-left-nav-primary" key="global-primary-nav" mode="inline" defaultSelectedKeys={ [ currentKey ] } tabIndex={ null } items={ menuItems } onClick={ handleNavigate } onKeyPress={ handleNavigate } selectable={ true } />;
    }

    return (
        <nav className={ `deck-global-left-nav ${ contentLoaded ? 'is-loaded' : '' }` }>
            <div className="close-icon" onClick={ toggleSidebar } onKeyPress={ toggleSidebar } tabIndex="0" role="button">
                <Close />
            </div>
            { Primary }
        </nav>
    );
};

export default LeftNav;

